"use client";

import { useEffect } from "react";
import { useForm } from "@tanstack/react-form";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

type BaseEntity = { id: number };

type ItemsListCardDialogProps<T extends BaseEntity, TCreate> = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: T | null;
  createSchema: any;
  editSchema: any;
  defaultCreateValues: TCreate;
  renderFormFields: (form: any) => React.ReactNode;
  onCreate: (data: TCreate) => Promise<void>;
  onUpdate: (data: T) => Promise<void>;
};

export function ItemsListCardDialog<
  T extends BaseEntity,
  TCreate = Omit<T, "id">,
>({
  open,
  onOpenChange,
  item,
  createSchema,
  editSchema,
  defaultCreateValues,
  renderFormFields,
  onCreate,
  onUpdate,
}: ItemsListCardDialogProps<T, TCreate>) {
  const isEdit = !!item;

  const form = useForm({
    defaultValues: (item ?? defaultCreateValues) as any,
    validators: {
      onSubmit: isEdit ? editSchema : createSchema,
    },
    onSubmit: async ({ value }) => {
      try {
        if (isEdit && item) {
          await onUpdate({ ...item, ...value });
        } else {
          await onCreate(value);
        }

        onOpenChange(false);
        form.reset();
      } catch {
        toast.error("Une erreur est survenue.");
      }
    },
  });

  useEffect(() => {
    if (item) {
      form.reset(item as any);
    } else {
      form.reset(defaultCreateValues as any);
    }
  }, [item]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          className="space-y-6"
        >
          {renderFormFields(form)}

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
                form.reset(defaultCreateValues);
              }}
            >
              Annuler
            </Button>

            <Button type="submit">{isEdit ? "Enregistrer" : "Créer"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
